import { useEffect, useState } from "react";

export interface WindowSize {
    height: number;
    width: number;
}

const throttlingDelayMillis = 100;

export const useWindowSize = (initialWindowSize?: WindowSize): WindowSize => {
    const [windowSize, setWindowSize] = useState<WindowSize>({
        height: initialWindowSize?.height ?? window.innerHeight,
        width: initialWindowSize?.width ?? window.innerWidth,
    });

    useEffect(() => {
        // this prohibits a warning about setting the state of an unmounted component
        let mounted = true;

        const onWindowResize = () => {
            if (mounted) {
                setWindowSize({ height: window.innerHeight, width: window.innerWidth });
            }
        };

        window.addEventListener("resize", throttle(onWindowResize, throttlingDelayMillis));

        return () => {
            window.removeEventListener("resize", onWindowResize);
            mounted = false;
        };
    });
    return windowSize;
};

function throttle<F extends (...args: unknown[]) => void>(f: F, delayMillis: number): F {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    return function (this: unknown, ...args: unknown[]) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const context = this;

        const delayThis = () => {
            timeoutId = undefined;
            f.apply(context, args);
        };

        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(delayThis, delayMillis);
    } as F;
}
